import { Injectable } from '@angular/core';
import { ActionGeneratorService, modelPortfoliosConfigGetters, ModelPortfoliosConfigState, ModelPortfoliosConfigStateNames } from '@ddt-web/common';
import { PartnerConstants, PartnerUserProfileService } from '@ddt-web/user-data';
import { WebToolsPlatformDataService } from '@ddt-web/web-tools-platform';
import { Action, select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';


@Injectable()
export class ModelPortfoliosConfigService {
	private modelPortfoliosConfig$: Observable<any>;
	private selectedScenarioKeys$: Observable<Array<string>>;
	private portfolioEditorVisible$: Observable<boolean>;
	private uniqueIdForPortfolioEditor$: Observable<string>;
	public blockDetailsCallAfterEdit$: Observable<boolean>;
	public blockDetailsCallAfterEdit: boolean;

	private modelPortfoliosConfig: any;

	constructor(
		private store: Store<ModelPortfoliosConfigState>,
		private webToolsPlatformDataService: WebToolsPlatformDataService,
		private partnerUserProfileService: PartnerUserProfileService,
		private actionGeneratorService: ActionGeneratorService
	) {
		this.retrieveAndLoadModelPortfoliosConfig();

		this.modelPortfoliosConfig$ = this.store.pipe(select(modelPortfoliosConfigGetters.getModelPortfoliosConfig()));
		this.selectedScenarioKeys$ = this.store.pipe(select(modelPortfoliosConfigGetters.getSelectedScenarioKeys()));
		this.portfolioEditorVisible$ = this.store.pipe(select(modelPortfoliosConfigGetters.getPortfolioEditorVisible()));
		this.uniqueIdForPortfolioEditor$ = this.store.pipe(select(modelPortfoliosConfigGetters.getUniqueIdForPortfolioEditor()));
		this.blockDetailsCallAfterEdit$ = this.store.pipe(select(modelPortfoliosConfigGetters.getBlockDetailsCallAfterEdit()));

		this.modelPortfoliosConfig$.subscribe(config => (this.modelPortfoliosConfig = config));
		this.blockDetailsCallAfterEdit$.subscribe(block => {
			this.blockDetailsCallAfterEdit = block;
		});
	}

	public retrieveAndLoadModelPortfoliosConfig(): void {
		this.partnerUserProfileService.getPartner().pipe(
			first(),
			switchMap(partner => {
				const shouldUseManagedModels = partner && PartnerConstants.MANAGED_MODELS_PARTNERS.includes(partner);
				if (shouldUseManagedModels) {
					return this.webToolsPlatformDataService.getManagedModelsConfigFromAPI();
				} else {
					return this.webToolsPlatformDataService.getModelPortfoliosConfigFromAPI();
				}
			}),
			map((config: any) => this.actionGeneratorService.loadOne(ModelPortfoliosConfigStateNames.modelPortfoliosConfig, config))
		).subscribe((action: Action) => this.store.dispatch(action));
	}

	public retrieveModelPortfoliosConfig(): void {
		// use loadAll here only for effect to listen to this action, in order to only load config once in view page
		this.store.dispatch(this.actionGeneratorService.loadAll(ModelPortfoliosConfigStateNames.modelPortfoliosConfig, null));
	}

	public updateSelectedScenarioKeys(uniqueIds: Array<string>): void {
		this.store.dispatch(this.actionGeneratorService.loadOne(ModelPortfoliosConfigStateNames.selectedScenarioKeys, uniqueIds));
	}

	public updatePortfolioEditorVisible(visible: boolean): void {
		this.store.dispatch(this.actionGeneratorService.loadOne(ModelPortfoliosConfigStateNames.portfolioEditorVisible, visible));
	}

	public updateBlockDetailsCallAfterEdit(visible: boolean): void {
		this.store.dispatch(this.actionGeneratorService.loadOne(ModelPortfoliosConfigStateNames.blockDetailsCallAfterEdit, visible));
	}

	public getBlockDetailsCallAfterEdit(): boolean {
		return this.blockDetailsCallAfterEdit;
	}

	public updateUniqueIdForPortfolioEditor(uniqueId: string): void {
		this.store.dispatch(this.actionGeneratorService.loadOne(ModelPortfoliosConfigStateNames.uniqueIdForPortfolioEditor, uniqueId));
	}

	public getPortfolioEditorVisible(): Observable<boolean> {
		return this.portfolioEditorVisible$;
	}

	public getUniqueIdForPortfolioEditor(): Observable<string> {
		return this.uniqueIdForPortfolioEditor$;
	}

	public getModelPortfoliosConfig$(): Observable<any> {
		return this.modelPortfoliosConfig$;
	}

	public getCurrentModelPortfoliosConfig(): any {
		return this.modelPortfoliosConfig;
	}

	public getSelectedScenarioKeys(): Observable<Array<string>> {
		return this.selectedScenarioKeys$;
	}
}
